import { ladderAccess, useServicesPeopleList, type ServicesPersonRow } from '@church/data'
import { peopleReach } from '@church/permissions'
import { faCrown, faFileExport, faUsers } from '@church/ui-icons'
import { createFileRoute, Link, redirect } from '@tanstack/react-router'
import { useWindowVirtualizer } from '@tanstack/react-virtual'
import { useEffect, useLayoutEffect, useRef, useState, type CSSProperties } from 'react'
import { DisplayOptions } from '../components/DisplayOptions.tsx'
import { MoreActions } from '../components/MoreActions.tsx'
import { PersonAvatar } from '../components/PersonAvatar.tsx'
import { pressableRow } from '../components/row-press.ts'
import {
  Card,
  Chip,
  EmptyState,
  ErrorState,
  Icon,
  InlineSpinner,
  Input,
  ListSkeleton,
} from '../components/ui.tsx'
import { apiBaseUrl } from '../lib/api.ts'
import { useBreadcrumbs } from '../lib/breadcrumbs.ts'
import { useListDisplay } from '../lib/display.ts'
import { sectionCrumbs } from '../lib/services-crumbs.ts'
import { remToPx } from '../lib/rem.ts'

/**
 * ForServices' People (docs/14): everyone for an Editor, with "On my teams"
 * and a CSV; the people on their own teams for a Viewer or Scheduler (a
 * leader's band). A Scheduled viewer has no People page: Plans instead.
 * Read at a glance — the name large, their teams as chips — each row
 * opening the person's profile. Virtualized: a church's whole directory.
 */
export const Route = createFileRoute('/_authed/_shell/services/_sections/people/')({
  beforeLoad: ({ context }) => {
    const held = ladderAccess(context.me)
    if (held.servicesLocked || peopleReach(held.access) === 'none')
      throw redirect({ to: '/services/plans', replace: true })
  },
  component: PeopleScreen,
})

/** What a row shows on a phone; every field shows from `sm` up. */
const ROW_FIELDS = [
  { id: 'teams', label: 'Teams', default: true },
  { id: 'positions', label: 'Positions', default: false },
  { id: 'campus', label: 'Campus', default: false },
] as const

/** 4rem: 64px on a phone, 56px on a computer (a 14px root). */
const ROW_HEIGHT = remToPx(4)

function PeopleScreen() {
  useBreadcrumbs(sectionCrumbs('People'))
  const [q, setQ] = useState('')
  const [mine, setMine] = useState(false)
  const list = useServicesPeopleList({ q, mine })
  const display = useListDisplay('services-people', ROW_FIELDS)
  const everyone = list.reach === 'everyone'
  const exportCsv = () => {
    if (list.exportHref) window.location.href = `${apiBaseUrl}${list.exportHref}`
  }

  return (
    <div className="space-y-4">
      {/* The filter row: search, "On my teams", and (an Editor's) the export. */}
      <div className="flex flex-wrap items-center gap-2">
        <Input
          className="min-w-0 flex-1 sm:max-w-xs"
          placeholder="Search by name…"
          aria-label="Search people"
          value={q}
          onChange={(event) => setQ(event.target.value)}
        />
        {everyone && (
          <button
            type="button"
            aria-pressed={mine}
            onClick={() => setMine((on) => !on)}
            className={`inline-flex min-h-10 shrink-0 items-center gap-2 rounded-full border px-3 text-sm font-medium transition-colors ${
              mine
                ? 'border-brand-solid bg-brand-soft text-content-brand'
                : 'border-border-default text-content-muted hover:bg-surface-sunken'
            }`}
          >
            <Icon icon={faUsers} className="size-3.5" />
            On my teams
          </button>
        )}
        {list.isFetching && !list.isLoading && (
          <span role="status" className="flex items-center text-content-brand">
            <InlineSpinner />
            <span className="sr-only">Loading…</span>
          </span>
        )}
        <DisplayOptions fields={ROW_FIELDS} shown={display.shown} onChange={display.set} />
        {everyone && (
          <div className="ml-auto flex items-center">
            <MoreActions actions={[{ label: 'Export', icon: faFileExport, onClick: exportCsv }]} />
          </div>
        )}
      </div>

      {list.people.length > 0 && (
        <p className="text-sm text-content-muted tabular-nums">
          {list.people.length}
          {list.complete ? '' : '+'} {list.people.length === 1 ? 'person' : 'people'}
          {!everyone && ' on your teams'}
        </p>
      )}

      {list.error && list.people.length === 0 ? (
        <Card bleed>
          <ErrorState
            error={list.error}
            safe="Nothing was changed."
            onRetry={() => void list.refetch()}
          />
        </Card>
      ) : list.isLoading ? (
        <Card bleed>
          <ListSkeleton rows={8} />
        </Card>
      ) : list.people.length === 0 ? (
        <Card bleed>
          <EmptyState
            title={
              q.trim()
                ? 'Nobody by that name'
                : mine
                  ? 'Nobody on your teams yet'
                  : everyone
                    ? 'No people yet'
                    : 'Nobody on your teams yet'
            }
            hint={
              !everyone && !q.trim()
                ? 'You see the people on the teams you serve on, lead or schedule.'
                : undefined
            }
          />
        </Card>
      ) : (
        <PeopleRows list={list} hide={display.hide} shown={display.shown} />
      )}
    </div>
  )
}

/** The rows, virtualized against the window (the page scrolls, not the card). */
function PeopleRows({
  list,
  hide,
  shown,
}: {
  list: ReturnType<typeof useServicesPeopleList>
  hide: (field: (typeof ROW_FIELDS)[number]['id']) => string
  shown: Record<(typeof ROW_FIELDS)[number]['id'], boolean>
}) {
  const rows = list.people
  const box = useRef<HTMLDivElement>(null)
  const [margin, setMargin] = useState(0)
  useLayoutEffect(() => {
    const measure = () =>
      setMargin((box.current?.getBoundingClientRect().top ?? 0) + window.scrollY)
    measure()
    window.addEventListener('resize', measure)
    return () => window.removeEventListener('resize', measure)
  }, [])
  const virtualizer = useWindowVirtualizer({
    count: rows.length,
    estimateSize: () => ROW_HEIGHT,
    overscan: 10,
    scrollMargin: margin,
  })
  const items = virtualizer.getVirtualItems()
  const last = items.at(-1)?.index ?? 0
  useEffect(() => {
    if (list.hasNextPage && !list.isFetchingNextPage && last >= rows.length - 15)
      void list.fetchNextPage()
  }, [last, rows.length, list])

  return (
    <Card bleed>
      {/* Column headings from sm up; a phone's row says what its display options choose. */}
      <div className="hidden grid-cols-[minmax(0,2fr)_minmax(0,3fr)_minmax(0,1fr)] gap-4 border-b border-border-default bg-surface-heading px-row-x py-2 text-xs font-medium tracking-wide text-content-muted uppercase sm:grid">
        <span>Name</span>
        <span>Teams</span>
        <span>Campus</span>
      </div>
      <div ref={box} className="relative" style={{ height: virtualizer.getTotalSize() }}>
        {items.map((item) => {
          const person = rows[item.index]
          if (!person) return null
          return (
            <PersonRow
              key={person.id}
              person={person}
              hide={hide}
              shown={shown}
              style={{
                height: ROW_HEIGHT,
                transform: `translateY(${item.start - virtualizer.options.scrollMargin}px)`,
              }}
            />
          )
        })}
      </div>
      {list.isFetchingNextPage && (
        <div className="flex justify-center border-t border-border-subtle py-3 text-content-brand">
          <InlineSpinner />
        </div>
      )}
    </Card>
  )
}

/** One person: the name large; their teams as chips (a crown where they lead); positions muted. */
function PersonRow({
  person,
  hide,
  shown,
  style,
}: {
  person: ServicesPersonRow
  hide: (field: (typeof ROW_FIELDS)[number]['id']) => string
  shown: Record<(typeof ROW_FIELDS)[number]['id'], boolean>
  style: CSSProperties
}) {
  const positions = person.teams.flatMap((team) => team.positions)
  // A phone's second line: what its display options choose, in words.
  const phoneLine = [
    shown.positions && positions.length > 0 ? positions.join(', ') : null,
    shown.campus ? person.campusName : null,
  ]
    .filter(Boolean)
    .join(' · ')
  return (
    <Link
      to="/services/people/$personId"
      params={{ personId: person.id }}
      className={`absolute inset-x-0 top-0 flex items-center gap-3 px-row-x sm:border-b sm:border-border-subtle no-underline sm:grid sm:grid-cols-[minmax(0,2fr)_minmax(0,3fr)_minmax(0,1fr)] sm:gap-4 ${pressableRow}`}
      style={style}
    >
      <span className="flex min-w-0 flex-1 items-center gap-3">
        <PersonAvatar
          personId={person.id}
          name={person.name}
          avatarUrl={person.avatarUrl}
          size="md"
        />
        <span className="min-w-0">
          <span className="block truncate text-base font-medium text-content-default">
            {person.name}
          </span>
          {phoneLine && (
            <span className="block truncate text-sm text-content-muted sm:hidden">{phoneLine}</span>
          )}
          {positions.length > 0 && (
            <span className="hidden truncate text-sm text-content-muted sm:block">
              {positions.join(', ')}
            </span>
          )}
        </span>
      </span>
      <span className={`flex min-w-0 shrink-0 items-center gap-1 overflow-hidden ${hide('teams')}`}>
        {/* Only the teams the reader sees: none here may still be a team elsewhere. */}
        {person.teams.map((team) => (
          <Chip key={team.id} tone={team.leads ? 'brand' : 'neutral'}>
            {team.leads && <Icon icon={faCrown} label="Leads" className="size-3" />}
            <span className="max-w-32 truncate">{team.name}</span>
          </Chip>
        ))}
      </span>
      <span className="hidden truncate text-sm text-content-muted sm:block">
        {person.campusName ?? ''}
      </span>
    </Link>
  )
}
